/**
 * Validation Store
 *
 * Manages workflow validation state including per-node errors.
 * Used for proactive validation (background validation as user edits)
 * and displaying validation badges on canvas nodes.
 */

import { create } from 'zustand';
import type { ValidationError } from '@/services/workflowValidationService';

/**
 * Validation error mapped to a specific node
 */
export interface NodeValidationError extends ValidationError {
  /** The node name this error applies to (e.g., "HTTP", "Compose") */
  nodeName: string;
}

interface ValidationState {
  /** Per-node validation errors: nodeName -> errors */
  nodeErrors: Record<string, ValidationError[]>;
  /** Global validation errors (not tied to specific nodes) */
  globalErrors: ValidationError[];
  /** Whether validation is currently running */
  isValidating: boolean;
  /** Timestamp of last validation */
  lastValidatedAt: number | null;
  /** Whether validation has been run at least once */
  hasValidated: boolean;
}

interface ValidationActions {
  /** Set validation errors from a validation result */
  setValidationErrors: (errors: ValidationError[]) => void;
  /** Clear all validation errors */
  clearValidationErrors: () => void;
  /** Clear errors for a specific node */
  clearNodeErrors: (nodeName: string) => void;
  /** Set validating state */
  setIsValidating: (isValidating: boolean) => void;
  /** Get errors for a specific node */
  getNodeErrors: (nodeName: string) => ValidationError[];
  /** Check if a node has errors */
  hasNodeErrors: (nodeName: string) => boolean;
  /** Get total error count */
  getTotalErrorCount: () => number;
}

type ValidationStore = ValidationState & ValidationActions;

/**
 * Parse validation error path to extract node name.
 * Paths can be like:
 * - "actions/HTTP" -> "HTTP"
 * - "triggers/manual" -> "manual"
 * - "actions/HTTP/inputs" -> "HTTP"
 * - "$schema" -> global (no node)
 */
function extractNodeNameFromPath(path?: string): string | null {
  if (!path) return null;

  // Match patterns like "actions/NodeName" or "triggers/NodeName"
  const actionMatch = path.match(/^actions\/([^/]+)/);
  if (actionMatch && actionMatch[1]) return actionMatch[1];

  const triggerMatch = path.match(/^triggers\/([^/]+)/);
  if (triggerMatch && triggerMatch[1]) return triggerMatch[1];

  return null;
}

/**
 * Categorize errors into node-specific and global errors
 */
function categorizeErrors(errors: ValidationError[]): {
  nodeErrors: Record<string, ValidationError[]>;
  globalErrors: ValidationError[];
} {
  const nodeErrors: Record<string, ValidationError[]> = {};
  const globalErrors: ValidationError[] = [];

  for (const error of errors) {
    const nodeName = extractNodeNameFromPath(error.path);

    if (nodeName) {
      if (!nodeErrors[nodeName]) {
        nodeErrors[nodeName] = [];
      }
      nodeErrors[nodeName].push(error);
    } else {
      globalErrors.push(error);
    }
  }

  return { nodeErrors, globalErrors };
}

export const useValidationStore = create<ValidationStore>((set, get) => ({
  // State
  nodeErrors: {},
  globalErrors: [],
  isValidating: false,
  lastValidatedAt: null,
  hasValidated: false,

  // Actions
  setValidationErrors: (errors: ValidationError[]) => {
    const { nodeErrors, globalErrors } = categorizeErrors(errors);
    set({
      nodeErrors,
      globalErrors,
      lastValidatedAt: Date.now(),
      hasValidated: true,
    });
  },

  clearValidationErrors: () => {
    set({
      nodeErrors: {},
      globalErrors: [],
    });
  },

  clearNodeErrors: (nodeName: string) => {
    const { nodeErrors } = get();
    const newNodeErrors = { ...nodeErrors };
    delete newNodeErrors[nodeName];
    set({ nodeErrors: newNodeErrors });
  },

  setIsValidating: (isValidating: boolean) => {
    set({ isValidating });
  },

  getNodeErrors: (nodeName: string) => {
    return get().nodeErrors[nodeName] || [];
  },

  hasNodeErrors: (nodeName: string) => {
    const errors = get().nodeErrors[nodeName];
    return errors !== undefined && errors.length > 0;
  },

  getTotalErrorCount: () => {
    const { nodeErrors, globalErrors } = get();
    let count = globalErrors.length;
    for (const errors of Object.values(nodeErrors)) {
      count += errors.length;
    }
    return count;
  },
}));
